import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,  // Send httpOnly cookies on every request
});

// Add request correlation ID to every request
api.interceptors.request.use((config) => {
  config.headers['X-Request-ID'] = Math.random().toString(36).substring(2, 14);
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve());
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;
    if (err.response?.status === 401 && !originalRequest._retry) {
      // Don't retry or redirect for auth check endpoints — let AuthContext handle them
      if (originalRequest.url?.includes('/auth/me') || originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(err);
      }
      // Don't retry login/register — show the error to the user
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/register')) {
        return Promise.reject(err);
      }
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve: () => resolve(api(originalRequest)), reject });
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr);
        localStorage.removeItem('ug_user');
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/terms') && !window.location.pathname.startsWith('/privacy')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// Mirror of backend/app/constants.py::CLIENT_STATUSES — keep the two identical.
export const CLIENT_STATUSES = {
  // "Processing", not "Order Submitted": at status 1 we are already checking the
  // order and downloading footage, and the label should reflect that rather than
  // describing the form the client filled in. See lib/clientStatusHelp.js for the
  // sentence the tooltip shows alongside it.
  // 2 is "Footage Link Checked" (Ryan, 2026-10-26): "Files Processed" read as "they have my footage now", and clients
  // deleted the link we edit from. The client's word only; the team keeps "Files Processed" (ADMIN_STATUSES, and
  // STAFF_CLIENT_STATUSES below for the analytics chart).
  1: "Processing", 2: "Footage Link Checked", 3: "Assigned to Editor",
  4: "Editing In Progress", 5: "First Draft Complete", 6: "Revisions In Progress",
  7: "Revisions Complete", 8: "Approved"
};

// Mirror of backend/app/constants.py::STAFF_CLIENT_STATUSES: the client track as the team names it.
export const STAFF_CLIENT_STATUSES = { ...CLIENT_STATUSES, 2: "Files Processed" };

export const ADMIN_STATUSES = {
  1: "Project Requested", 2: "Files Processed", 3: "Assigned to Editor",
  4: "Editing In Progress", 5: "First Draft Complete", 6: "Revisions In Progress",
  7: "Revisions Complete", 8: "Approved"
};

export const EDITOR_STATUSES = {
  '-1': "Pending Assignment", 0: "Assigned", 1: "Editing In Progress", 2: "First Draft Complete",
  3: "Revisions In Progress", 4: "New Draft Complete", 5: "Approved by Client"
};

/**
 * Status hues. These encode INFORMATION — a block of rows is found by colour
 * before it is read, and the group header strip paints a 13% tint of the same
 * value so header and pills agree. Do not flatten them to a shared palette.
 *
 * Every one is painted SOLID with white ink, so each must clear 4.5:1 against
 * white on its own. Seven already did (4.81 to 8.77). Two did not and were
 * darkened along their own hue, by uniform RGB scale so the colour identity is
 * unchanged:
 *
 *   2 Files Processed       #748B6B -> #677C5F   3.72 -> 4.55
 *   4 Editing In Progress   #9E8E66 -> #827454   3.22 -> 4.58
 *
 * If you add a status, check it against white here before shipping it.
 */
export const STATUS_COLORS = {
  '-1': '#4A4A5A', 1: '#5C6478', 2: '#677C5F', 3: '#7B6B8A', 4: '#827454',
  5: '#3E7B79', 6: '#7A3348', 7: '#5A7A5A', 8: '#2A6B6A'
};

export const LOYALTY_TIERS = [
  { name: "Standard", min: 0, max: 2999, discount: 0, color: "#94A3B8" },
  { name: "Topaz", min: 3000, max: 4999, discount: 10, color: "#D4A574" },
  { name: "Quartz", min: 5000, max: 9499, discount: 15, color: "#F5F5DC" },
  { name: "Amber", min: 9500, max: 13499, discount: 20, color: "#FFBF00" },
  { name: "Sapphire", min: 13500, max: 24999, discount: 25, color: "#0F52BA" },
  { name: "Emerald", min: 25000, max: 46999, discount: 30, color: "#50C878" },
  { name: "Ruby", min: 47000, max: 86499, discount: 35, color: "#E0115F" },
  { name: "Diamond", min: 86500, max: 999999999, discount: 40, color: "#B9F2FF" },
];

// A tier colour as text or as a graphic, on either theme: ONE rule, in lib/tierInk.js. It replaced a hand-picked
// light table for three tiers (Quartz, Amber, Diamond), which left Standard and Topaz unreadable on paper and
// Sapphire unreadable in dark.
export { tierInk } from './tierInk';

/** Strip pricing from product labels for non-admin display.
 *  Removes patterns like "(7,676 images × $0.06)" and "(50 × $2.50)".
 *  Leaves non-price parentheticals like "(up to 700)" untouched. */
export function sanitizeProductLabel(label, role) {
  if (!label) return label;
  if (role === 'admin') return label;
  // Strip only the PRICE — counts stay visible (P2 shorted-delivery root fix):
  // "Overage (500 × $0.25)" → "Overage (500)"; "Culling (3,002 images × $0.06)" → "Culling (3,002 images)"
  return label
    .replace(/\s*×\s*\$[\d.,]+(?=\))/g, '')
    .trim();
}
